import { UseMutationOptions } from "@tanstack/react-query";
import { useMutationApi, useQueryApi } from "api/hooks.ts";
import { formFieldApi } from "api/TenantSlug/FormField/api";
import { AxiosError } from "axios";
import { ApiErrorResponse } from "types/models.ts";

import GetFormFieldsRequest = App.Http.Requests.FormField.GetFormFieldsRequest;
import GetFormFieldsResponse = App.Http.Responses.FormField.GetFormFieldsResponse;
import GetFormFieldResponse = App.Http.Responses.FormField.GetFormFieldResponse;
import BatchStoreFormFieldRequest = App.Http.Requests.FormField.BatchStoreFormFieldRequest;
import BatchUpdateFormFieldRequest = App.Http.Requests.FormField.BatchUpdateFormFieldRequest;
import BatchInsertUpdateFormFieldResponse = App.Http.Responses.FormField.BatchInsertUpdateFormFieldResponse;
import BaseBatchDeleteRequest = App.Http.Requests.BaseBatchDeleteRequest;
import StoreFormFieldRequest = App.Http.Requests.FormField.StoreFormFieldRequest;
import UpdateFormFieldRequest = App.Http.Requests.FormField.UpdateFormFieldRequest;
import GetFormFieldUsagesResponse = App.Http.Responses.FormField.GetFormFieldUsagesResponse;

// Query to get a list of form fields
export const useGetFormFieldsQuery = (request: GetFormFieldsRequest, options = {}) => {
    return useQueryApi<GetFormFieldsResponse>({
        queryKey: ["getFormFields", request],
        queryFn: () => formFieldApi.index(request),
        ...options,
    });
};

// Query to get a specific form field
export const useGetFormFieldQuery = (formFieldId: string, request?: unknown, options = {}) => {
    return useQueryApi<GetFormFieldResponse>({
        queryKey: ["getFormField", formFieldId, request],
        queryFn: () => formFieldApi.show(formFieldId, request),
        ...options,
    });
};

export const useGetFormFieldUsagesQuery = (formFieldId: string, options = {}) => {
    return useQueryApi<GetFormFieldUsagesResponse>({
        queryKey: ["getFormFieldUsages", formFieldId],
        queryFn: () => formFieldApi.usages(formFieldId),
        ...options,
    });
};

export const useStoreFormFieldMutation = (
    options: UseMutationOptions<GetFormFieldResponse, AxiosError<ApiErrorResponse>, StoreFormFieldRequest> = {}
) => {
    return useMutationApi<GetFormFieldResponse, StoreFormFieldRequest>({
        mutationKey: ["storeFormField"],
        mutationFn: (request) => formFieldApi.store(request),
        ...options,
    });
};

export const useUpdateFormFieldMutation = (
    options: UseMutationOptions<
        GetFormFieldResponse,
        AxiosError<ApiErrorResponse>,
        { formFieldId: string; request: UpdateFormFieldRequest }
    > = {}
) => {
    return useMutationApi<GetFormFieldResponse, { formFieldId: string; request: UpdateFormFieldRequest }>({
        mutationKey: ["updateFormField"],
        mutationFn: ({ formFieldId, request }) => formFieldApi.update(formFieldId, request),
        ...options,
    });
};

export const useDestroyFormFieldMutation = (
    options: UseMutationOptions<void, AxiosError<ApiErrorResponse>, string> = {}
) => {
    return useMutationApi<void, string>({
        mutationKey: ["destroyFormField"],
        mutationFn: (formFieldId) => formFieldApi.destroy(formFieldId),
        ...options,
    });
};

// Mutation to batch insert form fields
export const useBatchInsertFormFieldsMutation = (
    options: UseMutationOptions<
        BatchInsertUpdateFormFieldResponse,
        AxiosError<ApiErrorResponse>,
        BatchStoreFormFieldRequest
    > = {}
) => {
    return useMutationApi<BatchInsertUpdateFormFieldResponse, BatchStoreFormFieldRequest>({
        mutationKey: ["batchInsertFormFields"],
        mutationFn: (request) => formFieldApi.batchInsert(request),
        ...options,
    });
};

// Mutation to batch update form fields
export const useBatchUpdateFormFieldsMutation = (
    options: UseMutationOptions<
        BatchInsertUpdateFormFieldResponse,
        AxiosError<ApiErrorResponse>,
        BatchUpdateFormFieldRequest
    > = {}
) => {
    return useMutationApi<BatchInsertUpdateFormFieldResponse, BatchUpdateFormFieldRequest>({
        mutationKey: ["batchUpdateFormFields"],
        mutationFn: (request) => formFieldApi.batchUpdate(request),
        ...options,
    });
};

// Mutation to batch delete form fields
export const useBatchDeleteFormFieldsMutation = (
    options: UseMutationOptions<void, AxiosError<ApiErrorResponse>, BaseBatchDeleteRequest> = {}
) => {
    return useMutationApi<void, BaseBatchDeleteRequest>({
        mutationKey: ["batchDeleteFormFields"],
        mutationFn: (request) => formFieldApi.batchDelete(request),
        ...options,
    });
};
